import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Briefcase, Plus, Edit2, Trash2, AlertCircle } from "react-feather";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";

import TableContainer from "../../BaseComponents/BaseTable";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseTooltip from "../../BaseComponents/BaseTooltip";

import {
  addPositionApi,
  listOfPositionApi,
  editPositionApi,
  deletePositionApi,
} from "../../Api/positionApi";
import { listOfOrganizationApi } from "../../Api/OrganizationApi";
import { SrNo, DeleteMessage, Position } from "../../Constant/Position/position";
import { Action, Search, SuperAdmin, notFound, organization } from "../../Constant";
import { checkStatusCodeSuccess, handleArrayResponse } from "../../Constant/common";
import { positionTitle } from "../../Constant/title";
import { validationMessages } from "../../Constant/validation";
import { hrEnums } from "../../Constant/HR_Staff/hrEnums";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { useSelectedOrganization } from "../OrganizationContext";
import { truncateText } from "../../Constant/Lead";

import { useTableQueryParams } from "../../utils/tableUtils";

/* ─── helpers ─────────────────────────────────────────────── */
const TipCell = (val, max = 32, color = "#6366f1") => {
  const t = truncateText(val, max);
  return val && val.length > max ? (
    <BaseTooltip label={val} position="top" maxWidth={300} color={color}>
      <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>{t}</span>
    </BaseTooltip>
  ) : (
    <span>{val || "—"}</span>
  );
};

/* ─── component ───────────────────────────────────────────── */
const PositionList = () => {
  useEffect(() => {
    document.title = positionTitle;
  }, []);

  const auth_role = localStorage.getItem("role");
  const auth_org_id = localStorage.getItem("organizationId");
  const isSuperAdmin = auth_role === SuperAdmin;
  const { setLoading } = usePageLoader();
  const { selectedOrganization } = useSelectedOrganization();
  const roleOrgId = isSuperAdmin ? selectedOrganization : auth_org_id ? Number(auth_org_id) : null;
  const formikRef = useRef(null);

  /* ── table query params ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5 });

  /* ── list state ── */
  const [positionList, setPositionList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);

  /* ── org dropdown ── */
  const [organizationList, setOrganizationList] = useState([]);

  /* ── modal state ── */
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [positionId, setPositionId] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);

  /* ── fetch org list ── */
  useEffect(() => {
    if (isSuperAdmin) {
      listOfOrganizationApi()
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            setOrganizationList(
              resp?.data?.listOfOrganizations?.map((item) => ({
                value: item.id,
                label: item.organization_name,
              })) || [],
            );
          }
        })
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  /* ── fetch positions ── */
  const fetchPositions = useCallback(() => {
    setLoading(true);
    listOfPositionApi({
      ...(roleOrgId && { condition: { organizationId: roleOrgId } }),
      order: [columnName, sortOrder],
      pageSize: customPageSize,
      pageNumber: currentPage,
      search: searchValue || "",
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setPositionList(resp?.data?.listOfPosition || []);
          setTotalPages(resp?.data?.totalPages || 1);
          setTotalRecords(resp?.data?.totalRecordsCount || 0);
          setTotalNumberOfRows(resp?.data?.numberOfRows || 0);
        } else {
          setPositionList([]);
          setTotalPages(1);
          setTotalRecords(0);
          setTotalNumberOfRows(0);
          toast.error(resp?.message);
        }
      })
      .catch(() => {
        setPositionList([]);
        setTotalPages(1);
        setTotalRecords(0);
        setTotalNumberOfRows(0);
      })
      .finally(() => setLoading(false));
  }, [roleOrgId, columnName, sortOrder, currentPage, customPageSize, searchValue, setLoading]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  /* ── open add ── */
  const openAdd = () => {
    formikRef.current?.resetForm?.();
    setEditId(null);
    setEditData(null);
    setShowFormModal(true);
  };

  /* ── open edit ── */
  const openEdit = useCallback(
    (id) => {
      const found = positionList.find((item) => item.id === id);
      setEditData(found || null);
      setEditId(id);
      setShowFormModal(true);
    },
    [positionList],
  );

  /* ── close form modal ── */
  const closeFormModal = () => {
    if (btnLoader) return;
    formikRef.current?.resetForm?.();
    setEditId(null);
    setEditData(null);
    setShowFormModal(false);
  };

  /* ── delete ── */
  const deletePosition = () => {
    setDeleteLoader(true);
    deletePositionApi(positionId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message);
          resetToFirstPage();
          fetchPositions();
          setShowDeleteModal(false);
          setPositionId(null);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || err?.message))
      .finally(() => setDeleteLoader(false));
  };

  /* ── formik ── */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      position_name: editId ? editData?.position_name || "" : "",
      organizationId: editId
        ? editData?.organization?.id || null
        : isSuperAdmin
          ? selectedOrganization || null
          : Number(localStorage.getItem("organizationId")) || null,
    },
    validationSchema: yup.object({
      position_name: yup.string().required(validationMessages.required(Position)),
      organizationId: isSuperAdmin
        ? yup.string().required(validationMessages.required("Organization")).nullable()
        : yup.string().nullable(),
    }),
    onSubmit: (values, { resetForm }) => {
      setBtnLoader(true);
      const payload = {
        position_name: values.position_name,
        organizationId: isSuperAdmin ? +values.organizationId : +auth_org_id,
      };
      const apiCall = editId ? editPositionApi(editId, payload) : addPositionApi(payload);

      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message);
            resetToFirstPage();
            fetchPositions();
            resetForm();
            setShowFormModal(false);
            setEditId(null);
            setEditData(null);
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  /* ── columns ── */
  const columns = useMemo(() => {
    const base = [
      {
        header: SrNo,
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        header: Position,
        accessorKey: "position_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.position_name, 32, "#6366f1"),
      },
    ];

    if (isSuperAdmin) {
      base.push({
        header: organization,
        accessorKey: "organization.organization_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.organization?.organization_name, 28, "#8b5cf6"),
      });
    }

    base.push({
      header: Action,
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const rec = row.original;
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <BaseTooltip label="Edit Position" position="top" color="#f59e0b">
              <button
                type="button"
                onClick={() => openEdit(rec.id)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  background: "rgba(245,158,11,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(245,158,11,0.22)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(245,158,11,0.12)";
                }}
              >
                <Edit2 size={14} strokeWidth={2} style={{ color: "#f59e0b" }} />
              </button>
            </BaseTooltip>
            <BaseTooltip label="Delete Position" position="top" color="#f43f5e">
              <button
                type="button"
                onClick={() => {
                  setPositionId(rec.id);
                  setShowDeleteModal(true);
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  background: "rgba(244,63,94,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(244,63,94,0.22)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(244,63,94,0.12)";
                }}
              >
                <Trash2 size={14} strokeWidth={2} style={{ color: "#f43f5e" }} />
              </button>
            </BaseTooltip>
          </div>
        );
      },
    });

    return base;
  }, [isSuperAdmin, openEdit, currentPage, customPageSize]);

  /* ── render ── */
  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Page header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Briefcase size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {Position}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} positions` : "Manage job positions"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={openAdd}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {hrEnums.ADD} {Position}
        </BaseButton>
      </div>

      {/* Table */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {positionList.length > 0 || searchValue ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={handlePageChange}
            currentPage={currentPage}
            fetchData={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            columns={columns}
            data={positionList}
            isGlobalFilter={true}
            shouldLineBreak={false}
            manualFiltering={true}
            manualPagination={true}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            SearchPlaceholder={Search}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <BaseModal
        isOpen={showFormModal}
        toggler={closeFormModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editId ? (
                <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              ) : (
                <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
              )}
            </div>
            <span>{editId ? "Edit Position" : "Add Position"}</span>
          </div>
        }
        submitText={editId ? "Update" : "Submit"}
        disabled={btnLoader}
        loader={btnLoader}
        submit={formik.handleSubmit}
      >
        <form onSubmit={formik.handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <BaseInput
              label="Position Name"
              name="position_name"
              type="text"
              placeholder="Enter position name"
              value={formik.values.position_name}
              touched={formik.touched.position_name}
              error={formik.errors.position_name}
              handleBlur={formik.handleBlur}
              handleChange={formik.handleChange}
              required
            />
            {isSuperAdmin && (
              <BaseSelect
                id="organization"
                name="organizationId"
                label="Organization"
                options={organizationList}
                placeholder="Select Organization"
                value={formik.values.organizationId}
                touched={formik.touched.organizationId}
                error={formik.errors.organizationId}
                handleBlur={() => formik.setFieldTouched("organizationId", true)}
                handleChange={(field, selected) => formik.setFieldValue(field, selected ?? null)}
                isDisabled={isSuperAdmin && !!selectedOrganization}
                required
              />
            )}
          </div>
        </form>
      </BaseModal>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={showDeleteModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(244,63,94,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Trash2 size={14} strokeWidth={2} style={{ color: "#f43f5e" }} />
            </div>
            <span>Delete Position</span>
          </div>
        }
        toggler={() => {
          if (!deleteLoader) {
            setShowDeleteModal(false);
            setPositionId(null);
          }
        }}
        submit={deletePosition}
        submitText="Delete"
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteMessage}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default PositionList;
